import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Header from './components/Header';
import Filters from './components/Filters';
import Stats from './components/Stats';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { ListFilters, DeveloperKPI } from '../../../interfaces/developer.interface';
import { buildHeadCells } from '../../../utils/methods';
import {
  GENERIC_LOOKUP_KEYS,
  VALIDATION_FIELD_MAPPING,
  HEAD_CELL_SORT_MAPPING,
} from '../../../utils/appConstant';
import useSorting from '../../../hooks/useSorting';
import usePagination from '../../../hooks/usePagination';
import useUrlParams from '../../../hooks/useUrlParams';
import useFetch from '../../../hooks/useFetch';
import DeveloperService from '../../../services/developer.service';
import CommonService from '../../../services/common.service';
import { SortOrder, Modules, AuthRoutes } from '../../../enums';
import { usePersistentFilters } from '../../../hooks/usePersistentFilters';

const List: React.FC = () => {
  const { t } = useTranslation();

  const defaultFilters: ListFilters = {
    searchText: '',
    status: '1',
    stateId: '',
    cityId: '',
  };

  const { initialFilters, saveFilters, resetFilters } = usePersistentFilters<ListFilters>('filters_developers', defaultFilters);

  const [filters, setFilters] = useState<ListFilters>(initialFilters);

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    status: '',
    state: '',
    city: '',
  });

  const headCells: HeadCellType[] = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.name,
      VALIDATION_FIELD_MAPPING.email,
      VALIDATION_FIELD_MAPPING.phoneNumber,
      VALIDATION_FIELD_MAPPING.addressCity,
      VALIDATION_FIELD_MAPPING.status,
      VALIDATION_FIELD_MAPPING.actions,
    ],
    'developerList'
  );

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.asc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.name,
  });
  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1,
  });

  useUrlParams({
    type: AuthRoutes.developers,
    uri: Modules.developers + AuthRoutes.developers,
    perPage,
    page,
    order,
    orderBy,
  });

  const {
    total,
    data: developers,
    loading,
  } = useFetch({
    service: DeveloperService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: HEAD_CELL_SORT_MAPPING.developerList[orderBy] || orderBy,
      order,
      searchText: filters.searchText.trim(),
      status: filters.status,
      stateId: filters.stateId,
      cityId: filters.cityId,
    },
    dependency: [
      order,
      orderBy,
      page,
      perPage,
      filters.searchText,
      filters.status,
      filters.stateId,
      filters.cityId,
    ],
  });

  const { data: allStates } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: {
      searchText: '',
      countryId: '1', // Defaulting to India
    },
    dependency: [],
  });

  const { data: states } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: {
      searchText: dropdownSearchData.state || '',
      countryId: '1',
    },
    dependency: [dropdownSearchData.state],
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: {
      searchText: '',
      stateId: filters.stateId,
    },
    dependency: [filters.stateId],
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: {
      searchText: dropdownSearchData.city || '',
      stateId: filters.stateId,
    },
    dependency: [dropdownSearchData.city, filters.stateId],
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: [],
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status],
  });

  const [kpiData, setKpiData] = useState<DeveloperKPI | null>(null);

  // ... (kpis fetch)
  useFetch({
    service: DeveloperService,
    func: 'kpis',
    payload: null,
    dependency: [],
    onSuccess: (data) => {
      setKpiData(data);
    },
  });

  const handleApplyFilters = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.name);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage, setOrder, setOrderBy]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.name);
    setFilters(defaultFilters);
    resetFilters();
  }, [resetFilters, setPage, setOrder, setOrderBy]);

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const headerSection = <TableHeaderSection total={total} />;

  const values: TableContextProvider = useMemo(() => {
    return {
      headerSection,
      row: Row,
      data: developers || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('developers.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    };
  }, [
    headerSection,
    t,
    headCells,
    order,
    orderBy,
    page,
    perPage,
    total,
    loading,
    developers,
    setPage,
    setPerPage,
    setOrder,
    setOrderBy,
  ]);

  return (
    <>
      <Header />
      <Stats data={kpiData} />
      <Filters
        status={status}
        allStatus={allStatus}
        states={states}
        allStates={allStates}
        cities={cities}
        allCities={allCities}
        dropdownSearchData={dropdownSearchData}
        initialFilters={initialFilters}
        onDropdownSearchChange={handleDropdownSearchInputChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />
      <TableBox tableValues={values} />
    </>
  );
};

export default List;
